import { useState, type FormEvent } from 'react';
import { errorMessage, isApiError } from '@/api/client';
import { useIntegrations, useRequestCustomDashboard, useRequestIntegration } from '@/api/hooks';
import { Dialog } from '@/components/Dialog';
import { useToast } from '@/components/toast-context';
import { Button, ErrorNote, TextArea, TextField } from '@/components/ui';
import { cx } from '@/lib/cx';

/** "Request an Integration": a platform that isn't in the library, or a vote for a Coming soon one. */
export function RequestIntegrationDialog({
  open,
  onClose,
  integration,
}: {
  open: boolean;
  onClose: () => void;
  integration?: { id: string; name: string } | null;
}) {
  const toast = useToast();
  const mutation = useRequestIntegration();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [kpis, setKpis] = useState('');
  const [notes, setNotes] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const formId = 'request-integration-form';

  async function submit(e: FormEvent) {
    e.preventDefault();
    const finalName = integration?.name ?? name.trim();
    if (!finalName) {
      setFields({ name: 'Which app should we add?' });
      return;
    }
    try {
      await mutation.mutateAsync({
        name: finalName,
        integration_id: integration?.id ?? null,
        website: website.trim() || null,
        kpis_wanted: kpis.trim() || null,
        notes: notes.trim() || null,
      });
      toast({
        title: 'Request sent',
        description: integration ? `We’ll let you know when ${integration.name} is ready.` : `Thanks! We count every request for ${finalName}.`,
      });
      setName('');
      setWebsite('');
      setKpis('');
      setNotes('');
      onClose();
    } catch (err) {
      if (isApiError(err)) setFields(err.fields);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={integration ? `Get ${integration.name} sooner` : 'Request an Integration'}
      description={
        integration
          ? `${integration.name} isn’t available yet. Requests decide what we build next.`
          : 'Tell us which app you use. The most requested ones get built first.'
      }
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
            Send request
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {mutation.isError && !Object.keys(fields).length && <ErrorNote message={errorMessage(mutation.error)} />}
        {!integration && (
          <TextField label="App name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} placeholder="HubSpot" autoFocus />
        )}
        {!integration && (
          <TextField label="Website" optional type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" error={fields.website} />
        )}
        <TextField
          label="Which numbers do you want to see?"
          optional
          value={kpis}
          onChange={(e) => setKpis(e.target.value)}
          placeholder="New contacts, deals won"
          error={fields.kpis_wanted}
        />
        <TextArea label="Anything else?" optional value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} error={fields.notes} />
      </form>
    </Dialog>
  );
}

/** "Request a Custom Dashboard": bespoke work, reviewed separately. */
export function CustomDashboardDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const mutation = useRequestCustomDashboard();
  const integrations = useIntegrations();
  const [description, setDescription] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const formId = 'custom-dashboard-form';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!description.trim()) {
      setError('Tell us what you’d like to see.');
      return;
    }
    setError(null);
    try {
      await mutation.mutateAsync({
        description: description.trim(),
        company: company.trim() || null,
        phone: phone.trim() || null,
        integrations: picked,
      });
      toast({ title: 'Request sent', description: 'We’ll get back to you by email within two business days.' });
      setDescription('');
      setCompany('');
      setPhone('');
      setPicked([]);
      onClose();
    } catch (err) {
      setError(isApiError(err) ? (err.fields.description ?? err.message) : errorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Request a Custom Dashboard"
      description="Need numbers MyDash doesn’t show yet, or a layout for your team? We build custom dashboards as a separate project."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
            Send request
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <TextArea
          label="What would you like to see?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error}
          rows={4}
          placeholder="One screen per location with sales, labour cost and reviews, sent every Monday."
          autoFocus
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Company" optional value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
          <TextField label="Phone" optional type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        </div>
        <fieldset>
          <legend className="text-sm font-medium text-ink">
            Apps involved <span className="font-normal text-muted-ink">(optional)</span>
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {integrations.data?.integrations.map((i) => {
              const on = picked.includes(i.id);
              return (
                <button
                  key={i.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setPicked((p) => (on ? p.filter((x) => x !== i.id) : [...p, i.id]))}
                  className={cx(
                    'rounded-full border px-3 py-1 text-sm transition-colors',
                    on ? 'border-ink bg-ink text-paper' : 'border-border bg-card text-ink hover:bg-muted',
                  )}
                >
                  {i.name}
                </button>
              );
            })}
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}
